import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

function hrefWithQuery(pathname: string, params: URLSearchParams) {
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export type UpdateSearchParamsOptions = {
  replace?: boolean;
};

export default function useQueryString(useRouterReplace = false) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const navigateToParams = useCallback(
    (nextParams: URLSearchParams, options?: UpdateSearchParamsOptions) => {
      const currentQuery = searchParams?.toString() || '';
      const nextQuery = nextParams.toString();
      if (nextQuery === currentQuery) return;
      const newPath = hrefWithQuery(pathname ?? '', nextParams);
      const replace = options?.replace ?? useRouterReplace;
      // Query-string updates must not jump the viewport; App Router scrolls by default.
      const navOptions = { scroll: false as const };
      replace ? router.replace(newPath, navOptions) : router.push(newPath, navOptions);
    },
    [searchParams, pathname, router, useRouterReplace],
  );

  const updateSearchParams = useCallback(
    (key: string, value: string | null, options?: UpdateSearchParamsOptions) => {
      const newSearchParams = new URLSearchParams(searchParams?.toString() || undefined);
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
      navigateToParams(newSearchParams, options);
    },
    [searchParams, navigateToParams],
  );

  const updateMultipleSearchParams = useCallback(
    (updates: Record<string, string | null>, options?: UpdateSearchParamsOptions) => {
      const newSearchParams = new URLSearchParams(searchParams?.toString() || undefined);

      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value);
        } else {
          newSearchParams.delete(key);
        }
      });

      navigateToParams(newSearchParams, options);
    },
    [searchParams, navigateToParams],
  );

  return { updateSearchParams, updateMultipleSearchParams, searchParams, pathname };
}
